import React, { useState } from 'react';
import {
  Users, Plus, MoreHorizontal, Search, Shield, ChevronRight,
  UserPlus, Settings, Trash2, Edit2, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';

const mockTeams = [
  {
    id: 'team-1',
    name: 'IT Operations',
    description: 'Core IT support and infrastructure team',
    memberCount: 12,
    workflowCount: 8,
    channels: ['#it-help', 'it-support@company.com'],
    managers: ['Sarah Chen', 'Mike Torres'],
    createdAt: '2024-01-15'
  },
  {
    id: 'team-2',
    name: 'Security',
    description: 'Security operations and incident response',
    memberCount: 6,
    workflowCount: 5,
    channels: ['#security-alerts'],
    managers: ['Alex Kim'],
    createdAt: '2024-01-20'
  },
  {
    id: 'team-3',
    name: 'Identity & Access',
    description: 'Identity management and access provisioning',
    memberCount: 4,
    workflowCount: 12,
    channels: ['#access-requests'],
    managers: ['Jordan Lee'],
    createdAt: '2024-02-01'
  },
  {
    id: 'team-4',
    name: 'Endpoint Management',
    description: 'Device provisioning and MDM operations',
    memberCount: 5,
    workflowCount: 6,
    channels: ['#device-help'],
    managers: ['Taylor Brown'],
    createdAt: '2024-02-15'
  }
];

const mockOrgAdmins = [
  { id: 'user-1', name: 'Emily Zhang', email: 'emily@company.com', avatar: 'EZ' },
  { id: 'user-2', name: 'David Park', email: 'david@company.com', avatar: 'DP' }
];

export const Teams = () => {
  const [teams, setTeams] = useState(mockTeams);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateTeam = () => {
    if (newTeam.name.trim()) {
      setTeams([...teams, {
        id: `team-${Date.now()}`,
        name: newTeam.name,
        description: newTeam.description,
        memberCount: 0,
        workflowCount: 0,
        channels: [],
        managers: [],
        createdAt: new Date().toISOString().split('T')[0]
      }]);
      setNewTeam({ name: '', description: '' });
      setShowCreateDialog(false);
    }
  };

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Teams</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage teams, assign roles, and configure team-specific workflows
              </p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Team
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Team</DialogTitle>
                  <DialogDescription>
                    Teams group people and workflows. Each team can have its own channels and routing rules.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="team-name">Team Name</Label>
                    <Input
                      id="team-name"
                      placeholder="e.g., IT Operations"
                      value={newTeam.name}
                      onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="team-description">Description</Label>
                    <Textarea
                      id="team-description"
                      placeholder="What does this team handle?"
                      value={newTeam.description}
                      onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateTeam}>Create Team</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search teams..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Badge variant="secondary">{filteredTeams.length} teams</Badge>
            </div>

            <div className="space-y-4">
              {filteredTeams.map((team) => (
                <Card key={team.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-foreground">{team.name}</h3>
                          <p className="text-sm text-muted-foreground mt-0.5">{team.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5" />
                              {team.memberCount} members
                            </span>
                            <span className="flex items-center gap-1">
                              <Settings className="w-3.5 h-3.5" />
                              {team.workflowCount} workflows
                            </span>
                          </div>
                          {team.managers.length > 0 && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-muted-foreground">Managers:</span>
                              {team.managers.map((manager, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">{manager}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm">
                          View Team
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Add Members
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit Team
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Settings className="w-4 h-4 mr-2" />
                              Team Settings
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Team
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="col-span-4 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Organization Admins
                </CardTitle>
                <CardDescription>
                  Org admins have full access to all teams and settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mockOrgAdmins.map((admin) => (
                    <div key={admin.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                        {admin.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{admin.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{admin.email}</p>
                      </div>
                      <Badge variant="secondary" className="text-xs">Admin</Badge>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="w-full mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Org Admin
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Role Permissions</CardTitle>
                <CardDescription>Two-level permission model</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Organization Level</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Org Admin</span>
                      <span className="text-foreground">Full access</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Member</span>
                      <span className="text-foreground">Team access only</span>
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t">
                  <h4 className="text-sm font-medium text-foreground mb-2">Team Level</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Manager</span>
                      <span className="text-foreground">Approve workflows</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Agent</span>
                      <span className="text-foreground">Use workflows</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teams;
